import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTRPC } from '../lib/trpc'
import { Button, Card, Notice, Page } from '../ui'
import { Choice } from '../ui/Choice'
import './consent.css'

/**
 * Where somebody decides what to lend a connected agent.
 *
 * The one screen in the app whose job is to be READ rather than used, so it
 * states what is about to happen in the words a person would use, and it makes
 * the answer an act rather than a default: nothing is ticked when it opens.
 * An agent that arrives asking for everything and is granted everything by a
 * pre-ticked list has not been agreed to, only clicked past.
 */
export function ConsentPage() {
  const trpc = useTRPC()
  const params = new URLSearchParams(window.location.search)
  const clientId = params.get('client_id') ?? ''
  const redirectUri = params.get('redirect_uri') ?? ''
  const codeChallenge = params.get('code_challenge') ?? ''
  const state = params.get('state')

  const spaces = useQuery(trpc.space.mine.queryOptions())
  const me = useQuery(trpc.auth.me.queryOptions())

  const [chosen, setChosen] = useState<string[]>([])
  const [platform, setPlatform] = useState<string[]>([])
  const [sending, setSending] = useState(false)
  const [failed, setFailed] = useState<string | null>(null)

  const incomplete = !clientId || !redirectUri || !codeChallenge
  if (incomplete) {
    return (
      <Page width="default">
        <div className="consent">
          <Card>
            <Notice tone="error">
              This link is missing what it needs to connect anything. Start
              again from the tool you are connecting.
            </Notice>
          </Card>
        </div>
      </Page>
    )
  }

  /*
   * The host, not the whole address. A person recognizes `claude.ai`; a
   * callback path with a query string is noise they would have to read past to
   * find the one word that tells them who this is.
   */
  const host = (() => {
    try {
      return new URL(redirectUri).host
    } catch {
      return redirectUri
    }
  })()

  const agree = async () => {
    setSending(true)
    setFailed(null)
    try {
      // Same-origin `/api`, so the dev proxy and the deployed origin both work
      // without a base-URL constant — the convention `pdfUrl` already keeps.
      const res = await fetch('/api/oauth/authorize', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          code_challenge: codeChallenge,
          space_ids: chosen,
          platform_access: platform.length > 0,
        }),
      })
      const body = (await res.json()) as {
        code?: string
        error_description?: string
        error?: string
      }
      if (!res.ok || !body.code) {
        setFailed(body.error_description ?? body.error ?? 'That did not work.')
        setSending(false)
        return
      }
      const back = new URL(redirectUri)
      back.searchParams.set('code', body.code)
      if (state) back.searchParams.set('state', state)
      window.location.href = back.toString()
    } catch {
      setFailed('Could not reach the server. Nothing was connected.')
      setSending(false)
    }
  }

  const options = (spaces.data ?? []).map((s) => ({
    value: s.spaceId,
    label: s.name,
    description:
      s.kind === 'personal'
        ? 'Your own books and drafts. No organization can see these.'
        : undefined,
  }))

  return (
    <Page width="default">
      <div className="consent">
        <Card>
          <h1 className="consent__title">Connect {host}</h1>
          <p className="consent__lead">
            It will act as {me.data?.name ?? 'you'}, and can do only what you
            can do. Choose what to let it reach.
          </p>

          <div className="consent__set">
            {spaces.isSuccess && options.length === 0 ? (
              <Notice>There is nothing to lend yet.</Notice>
            ) : (
              <Choice
                multiple
                label="Spaces"
                options={options}
                value={chosen}
                onValueChange={setChosen}
              />
            )}
          </div>

          {me.data?.isPlatformAdmin ? (
            <div className="consent__set">
              <Choice
                multiple
                label="The install itself"
                options={[
                  {
                    value: 'platform',
                    label: 'The tracker and install settings',
                    description:
                      'Separate from your spaces, and rarely needed. Leave this alone unless the agent is for developing the app itself.',
                  },
                ]}
                value={platform}
                onValueChange={setPlatform}
              />
            </div>
          ) : null}

          <p className="consent__note">
            You can end this at any time in Settings, and it stops the next call
            it makes.
          </p>

          {failed ? <Notice tone="error">{failed}</Notice> : null}

          <div className="uifoot">
            <span className="uifoot__state">
              {chosen.length === 0
                ? 'Nothing chosen yet.'
                : `${chosen.length} ${chosen.length === 1 ? 'space' : 'spaces'}${
                    platform.length ? ', plus the install' : ''
                  }.`}
            </span>
            <div className="uifoot__end">
              <Button
                disabled={
                  sending || (chosen.length === 0 && platform.length === 0)
                }
                onClick={agree}
              >
                {sending ? 'Connecting…' : 'Connect'}
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </Page>
  )
}
